import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import { fetchPermissions } from '@/stores/permissionsSlice';
import { pushPage } from '@/stores/sessionLogSlice';
import { updateMenu } from '@/stores/sideMenuSlice';
import { isBeaconAllowedPath } from '@/utils/beaconAccess';
import { ROLE } from '@/utils/enum';
import {
  checkAuthentication,
  getEncryptedClientId,
  getClientId,
  getRoleFromLocalStorage,
} from '@/utils/helper';
import { ReactElement, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import NavigateDefault from './DefaultNaivgate';

interface PrivateRouteProps {
  children: ReactElement;
}

function PrivateRoute({ children }: PrivateRouteProps) {
  const dispatch = useAppDispatch();
  const client = getClientId();
  const pageDetails = useAppSelector((state) => state.sessionLog.pageDetails);
  const pathname = window.location.pathname;
  const hasSentSessionRef = useRef(false);

  const { user } = useAppSelector((state) => state.users);

  useEffect(() => {
    dispatch(pushPage(pathname));
  }, [pathname]);

  // Resolve the caller's permissions once per authenticated session.
  const permissionStatus = useAppSelector((state) => state.permissions.status);
  const menuClientId = useAppSelector((state) => state.sideMenu.clientId);
  // 'failed' is otherwise terminal — nothing re-dispatches.
  const permissionRetries = useRef(0);
  useEffect(() => {
    if (permissionStatus === 'idle') {
      dispatch(fetchPermissions());
      return;
    }
    if (permissionStatus === 'failed' && permissionRetries.current < 2) {
      permissionRetries.current += 1;
      const delay = 400 * permissionRetries.current;
      const timer = setTimeout(() => dispatch(fetchPermissions()), delay);
      return () => clearTimeout(timer);
    }
  }, [dispatch, permissionStatus]);

  // The menu is built from a non-reactive registry, so rebuild it once the set
  // lands or permission-gated entries stay hidden until the next navigation.
  // The id comes from the store, not getClientId(): looking it up afresh
  // resurrects the last-visited client for a Super Owner sitting on /clients.
  const permissionsResolved =
    permissionStatus === 'ready' || permissionStatus === 'failed';
  useEffect(() => {
    if (permissionsResolved) dispatch(updateMenu(menuClientId));
  }, [dispatch, permissionsResolved, menuClientId]);

  useEffect(() => {
    const sendSessionLog = () => {
      if (hasSentSessionRef.current) return;
      if (!pageDetails?.length) return;

      const payload = {
        client: user?.data.user.organization_id || getClientId(),
        clientTeam: user?.data.user.email,
        sessionStart: pageDetails[0]?.time,
        sessionEnd: new Date().toISOString(),
        pageDetails,
      };

      if (!payload.client || !payload.clientTeam) return;

      hasSentSessionRef.current = true;

      const baseUrl = (import.meta.env.VITE_BASE_URL || '').replace(/\/$/, '');
      const url = `${baseUrl}/sessionLog/create`;
      const body = JSON.stringify(payload);

      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        const accepted = navigator.sendBeacon(url, blob);
        if (accepted) return;
      }

      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
        credentials: 'include',
      }).catch(() => {});
    };

    const handlePageHide = () => {
      sendSessionLog();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendSessionLog();
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [pageDetails, user]);

  const role = getRoleFromLocalStorage()?.toUpperCase();
  if (role === ROLE.BEACON && !isBeaconAllowedPath(pathname)) {
    const encryptedClientId = getEncryptedClientId();
    if (encryptedClientId) {
      return <Navigate to={`/${encryptedClientId}/beacon`} replace />;
    }
    return <NavigateDefault />;
  }

  // Dev: skip the auth gate entirely. The Beacon prototype runs without
  // the Antares Backend (no /auth/login endpoint), so a real
  // checkAuthentication() always returns false and we'd redirect every
  // page to /login. No-op in production builds.
  if (import.meta.env.DEV) return children;

  const isAuthenticated = checkAuthentication();
  if (!isAuthenticated) {
    // already on login page, don't redirect again
    if (window.location.pathname === '/login') return children;
    return <Navigate to='/login' replace />;
  }

  return children;
}

export default PrivateRoute;
